// import { useDropzone } from 'react-dropzone';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
// import { toast } from 'react-toastify';
import { FormMode } from './signup.types';
// import { SUPPORTED_IMAGE_MIME_TYPES } from '../../config/const';
import InputAdornment from '@mui/material/InputAdornment';
import { useCallback, useState, useEffect, useRef } from 'react';
import { suggestUsernames } from '../../utils/api';

interface CreatorOnBoardingStepOneProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: any;
  handleBlur: any;
  setFieldValue: any;
  setTouched: any;
  setErrors: any;
  isValid: any;
  dirty: any;
  formSubmitting: boolean;
  formMode: FormMode;
}

const AGE = [
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25-34' },
  { value: '35-44', label: '35-44' },
  { value: '45-54', label: '45-54' },
  { value: '55-64', label: '55-64' },
  { value: '65-74', label: '65-74' },
  { value: '75-84', label: '75-84' },
  { value: '85+', label: '85+' },
];

const GENDER = [
  { value: 'Female', label: 'Female' },
  { value: 'Male', label: 'Male' },
  { value: 'Non-Binary', label: 'Non-Binary' },
  { value: 'Gender Fluid', label: 'Gender Fluid' },
  { value: 'A gender not listed', label: 'A gender not listed' },
  { value: 'Unsure how to describe', label: 'Unsure how to describe' },
  { value: 'Prefer not to say', label: 'Prefer not to say' },
];

const ETHNICITY = [
  { value: 'American Indian or Alaska Native', label: 'American Indian or Alaska Native' },
  { value: 'Asian', label: 'Asian' },
  { value: 'Black or African American', label: 'Black or African American' },
  { value: 'Hispanic or Latino', label: 'Hispanic or Latino' },
  { value: 'Middle Eastern or North African', label: 'Middle Eastern or North African' },
  { value: 'Native Hawaiian or Other Pacific Islander', label: 'Native Hawaiian or Other Pacific Islander' },
  { value: 'White', label: 'White' },
  { value: 'Mixed / Multiple ethnicities', label: 'Mixed / Multiple ethnicities' },
  { value: 'Other', label: 'Other' },
  { value: 'Prefer not to say', label: 'Prefer not to say' },
]

export default function CreatorOnBoardingStepOne(
  props: CreatorOnBoardingStepOneProps
) {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setTouched,
    formSubmitting,
    formMode,
  } = props;

  // Username suggestions state
  const [usernameSuggestions, setUsernameSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Fetch username suggestions when fullName changes
  useEffect(() => {
    // Only fetch suggestions if username is empty and fullName has value
    if (!values.userName && values.fullName && values.fullName.trim().length >= 2) {
      // Debounce the API call
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(async () => {
        setLoadingSuggestions(true);
        try {
          const response = await suggestUsernames(values.fullName, 5);
          if (response.ok) {
            const data = await response.json();
            setUsernameSuggestions(data.suggestions || []);
            // Auto-select the first suggestion if available and username is empty
            if (data.suggestions?.length > 0 && !values.userName) {
              setFieldValue('userName', data.suggestions[0]);
            }
          }
        } catch (error) {
          console.error('Error fetching username suggestions:', error);
        } finally {
          setLoadingSuggestions(false);
        }
      }, 500);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [values.fullName]); // eslint-disable-line react-hooks/exhaustive-deps

  // const onDrop = useCallback(
  //   async (acceptedFiles: any) => {
  //     const isValid = fileValidator(acceptedFiles[0]);
  //     if (!isValid) {
  //       toast.error('Please select a valid banner image');
  //       setTouched({ ...touched, bannerPhoto: true });
  //       setFieldValue('bannerPhoto', '');
  //     }
  //     if (isValid) {
  //       setFieldValue('bannerPhoto', acceptedFiles[0]);
  //     }
  //   },
  //   [touched, setTouched, setFieldValue]
  // );

  // const fileValidator = (file: any) => {
  //   if (!SUPPORTED_IMAGE_MIME_TYPES.includes(file?.type)) {
  //     return false;
  //   }
  //   return true;
  // };

  // const { getRootProps, getInputProps, isDragActive } = useDropzone({
  //   onDrop,
  //   accept: {
  //     'image/*': ['.jpeg', '.jpg', '.png'],
  //   },
  //   maxFiles: 1,
  // });

  const avatarInput = useCallback(
    (values, touched, setTouched, setFieldValue) => {
      const profileUploaded =
        values?.profilePhoto &&
          typeof values?.profilePhoto === 'object' &&
          values?.profilePhoto?.name
          ? URL.createObjectURL(values?.profilePhoto)
          : values?.profilePhoto && typeof values?.profilePhoto === 'string'
            ? values?.profilePhoto
            : '';

      return (
        <Box
          borderRadius='12px'
          display='flex'
          gap={1}
          flexDirection='column'
          alignItems='flex-start'
          width='100%'
          position='relative'
        >
          <input
            type='file'
            accept='image/*,image/heif,image/heic'
            id='file-input-profile-photo'
            name='profilePhoto'
            hidden
            onChange={(event) => {
              setTouched({ ...touched, profilePhoto: true });
              setFieldValue('profilePhoto', event?.currentTarget?.files?.[0]);
            }}
          />
          <label htmlFor='file-input-profile-photo' style={{ cursor: 'pointer' }}>
            <Box
              display="flex"
              alignItems="center"
              gap={2} // Add space between avatar and text
              width="100%"
            >
              <Avatar
                alt={values?.fullName}
                src={profileUploaded}
                sx={{
                  width: "193px", // Explicitly set width
                  height: "193px", // Explicitly set height
                  borderRadius: "78px", // Custom border-radius
                  border: "1px solid rgba(66, 66, 66, 0.5)", // Outlined border
                  opacity: 1, // Ensure opacity is not 0
                  objectFit: "cover", // Ensure the image fits properly
                  fontSize: "2.375rem",
                }}
              >
                {values?.fullName ? values?.fullName[0] : ''}
              </Avatar>
            </Box>
          </label>
        </Box>
      );
    },
    []
  );

  const getSocialMediaPrefix = (socialKey) => {
    switch (socialKey) {
      case 'linkedIn':
        return 'https://www.linkedin.com/in/';
      case 'youtube':
        return 'https://www.youtube.com/@';
      case 'tiktok':
        return 'https://www.tiktok.com/@';
      case 'instagram':
        return 'https://www.instagram.com/';
      default:
        return '';
    }
  };

  return (
    <Box
      display='flex'
      flex={1}
      flexDirection='column'
      alignItems='Left'
      justifyContent='left'
      width="100%"
      gap={4}
      sx={{
        px: 1,
      }}
    >
      <Box width='100%'>
        {avatarInput(values, touched, setTouched, setFieldValue)}
        {touched?.profilePhoto && Boolean(errors?.profilePhoto) && (
          <Typography
            variant='body1'
            color={'error'}
            mt={1}
          >
            {errors?.profilePhoto as string}
          </Typography>
        )}
      </Box>

      {/* Basic Info Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 2,
        }}
      >
        <Box sx={{ width: '100%' }}> {/* Name*/}
          <Typography
            variant="h7" // Changed from "h6" to "h7" as per screenshot
            sx={{
              mb: 1, // Space below the label
            }}
          >
            Name <Typography variant='body2' component='span'>*Required</Typography>
          </Typography>

          {/* Text Box */}
          <TextField
            fullWidth
            id="text-input-fullName"
            name="fullName"
            value={values.fullName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.fullName && Boolean(errors.fullName)}
            helperText={touched.fullName && errors.fullName}
            size="small"
            margin="dense"
            sx={{
              mt: 1,
            }}
          />
        </Box>

        <Box sx={{ width: '100%' }}> {/* Username */}
          <Typography
            variant="h7"
            sx={{
              mb: 1,
            }}
          >
            Username
            {loadingSuggestions && (
              <CircularProgress size={14} sx={{ ml: 1, verticalAlign: 'middle' }} />
            )}
          </Typography>

          {/* Autocomplete for Username with suggestions */}
          <Autocomplete
            freeSolo
            id="text-input-userName"
            options={usernameSuggestions}
            value={values.userName || ''}
            onChange={(_event, newValue) => {
              setFieldValue('userName', newValue || '');
            }}
            onInputChange={(_event, newInputValue) => {
              setFieldValue('userName', newInputValue);
            }}
            disabled={formSubmitting}
            loading={loadingSuggestions}
            renderInput={(params) => (
              <TextField
                {...params}
                name="userName"
                placeholder="Choose a username"
                size="small"
                margin="dense"
                error={touched.userName && Boolean(errors.userName)}
                helperText={
                  (touched.userName && errors.userName) ||
                  (usernameSuggestions.length > 0 ? 'Select from suggestions or type your own' : '')
                }
                onBlur={handleBlur}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSuggestions ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            sx={{
              mt: 1,
            }}
          />
        </Box>

        <Box sx={{ width: '100%' }}> {/* Occupation */}
          <Typography variant="h7" >
            Occupation <Typography variant='body2' component='span'>*Required</Typography>
            <Typography variant='body2' color='text.secondary' sx={{mt: 0.5}}>
              (50 Character Max)
            </Typography>
          </Typography>

          {/* Text Field for Occupation */}
          <TextField
            type='string'
            fullWidth
            id="text-input-occupation"
            name="occupation"
            value={values.occupation}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.occupation && Boolean(errors.occupation)}
            helperText={touched.occupation && errors.occupation}
            size="small"
            margin="dense"
            sx={{
              mt: 1,
            }}
          />
        </Box>

        <Box sx={{ width: '100%' }}> {/* Biography */}
          <Typography variant="h7" >
            Biography
            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
              Tell other users about yourself.
            </Typography>
          </Typography>

          {/* Text Field for Biography */}
          <TextField
            fullWidth
            multiline
            rows={3}
            id="text-input-shortBio"
            name="shortBio"
            value={values.shortBio}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.shortBio && Boolean(errors.shortBio)}
            helperText={touched.shortBio && errors.shortBio}
            margin="dense"
            sx={{
              mt: 1,
            }}
          />
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" width="100%"> {/* Social Media Section */}
        <Typography
          variant="h6"
          color='text.primary'
          sx={{
            mb: 0.5,
          }}
        >
          Share Social Accounts
        </Typography>

        {/* Section Description */}
        <Typography
          variant="body2"
          color='text.secondary'
          sx={{
            mb: 2,
          }}
        >
          Allow others to follow you on different channels.
        </Typography>

        {/* Social Media Inputs */}
        <Box display="flex" flexDirection="column" gap={2} width="100%">
          {Object.entries({
            linkedIn: 'LinkedIn',
            youtube: 'YouTube',
            tiktok: 'TikTok',
            instagram: 'Instagram',
          }).map(([socialKey, socialLabel]) => (
            <Box key={socialKey} sx={{ display: 'flex', flexDirection: 'column' }}>
              {/* Social Media Label */}
              <Typography
                variant="h7"
              >
                {socialLabel}
              </Typography>

              {/* Social Media Input Field */}
              <TextField
                disabled={formSubmitting}
                fullWidth
                id={`text-input-${socialKey}`}
                placeholder={``}
                name={`${socialKey}URL`}
                value={values[`${socialKey}URL`]}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
                margin="dense"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography
                        >
                          {getSocialMediaPrefix(socialKey)}
                        </Typography>
                      </InputAdornment>
                    ),
                  }
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {formMode === "update" && (
        <Box display="flex" flexDirection="column" width="100%">
          {/* Section Title */}
          <Typography
            variant="h6"
            sx={{
              mb: 0.5,
            }}
          >
            Background Information
          </Typography>

          {/* Section Description */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
            }}
          >
            This information will NOT be visible on your profile. It is used to help recommend your AI persona.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: {xs: 'column', md: 'row'},
              gap: 2,
              width: '100%',
              mb: 3,
              alignItems: 'flex-start',
            }}
          >
            {/* Age */}
            <Box display="flex" flexDirection="column" sx={{flex: 1, width: {xs: '100%', md: 'auto'}}}>
              <Typography variant="h7" >
                Age Group <Typography variant='body2' component='span' ></Typography>
              </Typography>

              <FormControl fullWidth>
                <Select
                  fullWidth
                  displayEmpty
                  id="select-input-age"
                  name="age"
                  value={values.age}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.age && Boolean(errors.age)}
                  sx={{
                    mt: 1,
                  }}
                >
                  {AGE.map((ageItem, index) => (
                    <MenuItem key={index} value={ageItem.value}>
                      {ageItem.label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.age && errors.age && (
                  <FormHelperText error>{errors.age}</FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* Gender */}
            <Box display="flex" flexDirection="column" sx={{ flex: 1, width: {xs: '100%', md: 'auto'} }}>
              <Typography variant="h7" >
                Gender <Typography variant='body2' component='span' ></Typography>
              </Typography>

              {/* Select Field for Gender */}
              <FormControl fullWidth>
                <Select
                  fullWidth
                  displayEmpty
                  id="select-input-gender"
                  name="gender"
                  value={values.gender}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.gender && Boolean(errors.gender)}
                  sx={{
                    mt: 1,
                  }}
                >
                  {GENDER.map((genderItem, index) => (
                    <MenuItem key={index} value={genderItem.value}>
                      {genderItem.label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.gender && errors.gender && (
                  <FormHelperText error>{errors.gender}</FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* Ethnicity */}
            <Box display="flex" flexDirection="column" sx={{ flex: 1, width: {xs: '100%', md: 'auto'} }}>
              <Typography variant="h7" >
                Ethnicity
              </Typography>

              {/* Select Field for Ethnicity */}
              <FormControl fullWidth>
                <Select
                  fullWidth
                  displayEmpty
                  id="select-input-ethnicity"
                  name="ethnicity"
                  value={values.ethnicity}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.ethnicity && Boolean(errors.ethnicity)}
                  sx={{
                    mt: 1,
                  }}
                >
                  {ETHNICITY.map((ethnicityItem, index) => (
                    <MenuItem key={index} value={ethnicityItem.value}>
                      {ethnicityItem.label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.ethnicity && errors.ethnicity && (
                  <FormHelperText error>{errors.ethnicity}</FormHelperText>
                )}
              </FormControl>
            </Box>
          </Box>

          {/* Birthplace */}
          <Box display="flex" flexDirection="column" mb="24px" width="100%">
            <Typography
              variant="h7" // Changed from "h6" to "h7" as per screenshot
              sx={{
                fontWeight: 'fontWeightSemiBold', // Ensure the weight matches as 'SemiBold'
                fontSize: '16px', // Updated to match screenshot
                lineHeight: '19.2px', // Set line-height as per screenshot
                letterSpacing: '0.15px', // Added letter spacing
                color: 'text.primary', // Use theme color for text
                mb: 1, // Space below the label
              }}
            >
              Birthplace
              <Typography variant='body2' color='text.secondary' sx={{mt: 0.5}}>
                City, State, Country
              </Typography>
            </Typography>
            <TextField
              fullWidth
              disabled={formSubmitting}
              name="birthPlace"
              id="text-input-birthPlace"
              value={values.birthPlace}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.birthPlace && Boolean(errors.birthPlace)}
              helperText={touched.birthPlace && errors.birthPlace}
              placeholder="Ex: Chicago, IL, United States"
              margin="dense"
            />
          </Box>


          {/* Current Residence */}
          <Box display="flex" flexDirection="column" mb="24px" width="100%">
            <Typography
              variant="h7" // Changed from "h6" to "h7" as per screenshot
              sx={{
                mb: 1, // Space below the label
              }}
            >
              Current Residence
              <Typography variant='body2' color='text.secondary' sx={{mt: 0.5}}>
                City, State, Country
              </Typography>
            </Typography>
            <TextField
              fullWidth
              disabled={formSubmitting}
              name="residencePlace"
              id="text-input-residencePlace"
              value={values.residencePlace}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.residencePlace && Boolean(errors.residencePlace)}
              helperText={touched.residencePlace && errors.residencePlace}
              placeholder="Ex: New York City, NY, United States"
              margin="dense"
            />
          </Box>
        </Box>

      )}

    </Box>
  );
}
